import { useEffect, useMemo, useState } from "react";
import type {
  PublicEventDetailDto,
  PublicEventsResponseDto,
  PublicScheduleResponseDto,
  PublicTenantDto
} from "@madamuh/domain";
import { Link, useParams } from "react-router-dom";
import { fetchPublicEvent, fetchPublicEvents, fetchPublicSchedule, fetchPublicTenant } from "../../shared/api/public";
import { formatTenantDateTime, formatTenantDateTimeRange, formatTenantMonthHeading } from "../../shared/lib/tenantDateTime";
import { GENERIC_TENANT_LABELS, getTenantLabels, type TenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import { PageScaffold } from "../../shared/ui/PageScaffold";

const DEFAULT_PUBLIC_TENANT_TITLE = "MADAMUH LESSON";
const DEMO_TENANT_SLUG = "demo-club";

function buildDemoHomeNotifications(labels: TenantLabels) {
  return [
  `次回${labels.lesson}の持ち物サンプルです。`,
  "公開イベントのお知らせサンプルです。"
] as const;
}

function buildDemoHomeVideos(labels: TenantLabels) {
  return [
    { title: "リズムトレーニング", description: `自宅で見返せる${labels.lesson}動画のサンプルです。` },
    { title: "ストレッチ", description: `${labels.lesson}前後に使う補助動画のサンプルです。` }
  ] as const;
}

type PublicTenantState = {
  tenant: PublicTenantDto | null;
  loadError: string | null;
  loadState: "idle" | "loading" | "error";
};

function resolvePublicTenantTitle(tenant: PublicTenantDto | null): string {
  return tenant?.brandName ?? tenant?.displayName ?? DEFAULT_PUBLIC_TENANT_TITLE;
}

function buildPublicLinks(tenantSlug: string, tenant: PublicTenantDto | null) {
  const tenantLabels = resolvePublicTenantLabels(tenant);

  return [
    { to: `/t/${tenantSlug}`, label: "公開トップ" },
    ...(tenant?.publicSettings.showPublicSchedule
      ? [{ to: `/t/${tenantSlug}/public/schedule`, label: `公開${tenantLabels.lesson}予定` }]
      : []),
    ...(tenant?.publicSettings.showPublicEvents ? [{ to: `/t/${tenantSlug}/public/events`, label: "公開イベント" }] : []),
    ...(tenant?.isDemoTenant ? [{ to: `/t/${tenantSlug}/demo`, label: "デモを見る" }] : []),
    { to: `/t/${tenantSlug}/entry`, label: "利用開始" }
  ];
}

function canUsePublicDemoViewer(tenantSlug: string, tenant: PublicTenantDto | null): boolean {
  return tenantSlug === DEMO_TENANT_SLUG && tenant?.isDemoTenant === true;
}

function resolvePublicTenantLabels(tenant: PublicTenantDto | null) {
  return tenant ? getTenantLabels(tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
}

function usePublicTenantState(tenantSlug: string): PublicTenantState {
  const [tenant, setTenant] = useState<PublicTenantDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadTenant() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextTenant = await fetchPublicTenant(tenantSlug);

        if (cancelled) {
          return;
        }

        setTenant(nextTenant);
        setLoadState("idle");
      } catch {
        if (cancelled) {
          return;
        }

        setTenant(null);
        setLoadState("error");
        setLoadError("公開情報の取得に失敗しました。");
      }
    }

    void loadTenant();

    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  return {
    tenant,
    loadError,
    loadState
  };
}

function renderFlags(flags: PublicScheduleResponseDto["items"][number]["displayFlags"], labels: TenantLabels) {
  const flagLabels = [
    flags.isCancelled ? "休講" : null,
    flags.isRescheduled ? "振替" : null,
    flags.isStudioTbd ? `${labels.studio}未定` : null,
    flags.isTeacherTbd ? `${labels.teacher}未定` : null,
    flags.isSubstitute ? "代講" : null
  ].filter((value): value is string => value !== null);

  if (flagLabels.length === 0) {
    return null;
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {flagLabels.map((label) => (
        <span
          key={label}
          style={{
            fontSize: 12,
            padding: "4px 8px",
            borderRadius: 999,
            background: "#f3f4f6"
          }}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function PublicTenantHeader({
  tenantSlug,
  tenant
}: {
  tenantSlug: string;
  tenant: PublicTenantDto | null;
}) {
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <p style={{ margin: 0 }}>
        {resolvePublicTenantTitle(tenant)} の公開情報です。公開{resolvePublicTenantLabels(tenant).lesson}予定やイベントはログインなしで確認できます。
      </p>
      <div style={{ display: "grid", gap: 10 }}>
        {tenant?.publicSettings.showPublicSchedule ? (
          <Link
            to={`/t/${tenantSlug}/public/schedule`}
            style={{
              display: "block",
              padding: 12,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              textDecoration: "none",
              color: "inherit"
            }}
          >
            公開スケジュールを見る
          </Link>
        ) : null}
        {tenant?.publicSettings.showPublicEvents ? (
          <Link
            to={`/t/${tenantSlug}/public/events`}
            style={{
              display: "block",
              padding: 12,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              textDecoration: "none",
              color: "inherit"
            }}
          >
            公開イベントを見る
          </Link>
        ) : null}
        {tenant?.isDemoTenant ? (
          <Link
            to={`/t/${tenantSlug}/demo`}
            style={{
              display: "block",
              padding: 12,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              textDecoration: "none",
              color: "inherit"
            }}
          >
            デモを見る
          </Link>
        ) : null}
        <Link
          to={`/t/${tenantSlug}/entry`}
          style={{
            display: "block",
            padding: 12,
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            textDecoration: "none",
            color: "inherit"
          }}
        >
          利用開始
        </Link>
      </div>
    </div>
  );
}

function renderPublicAttachmentCard(attachment: PublicEventDetailDto["attachments"][number]) {
  const linkLabel =
    attachment.attachmentType === "pdf"
      ? "PDFを開く"
      : attachment.attachmentType === "video"
        ? "動画を開く"
        : "画像を開く";

  return (
    <article
      key={attachment.attachmentId}
      style={{
        display: "grid",
        gap: 8,
        padding: 12,
        border: "1px solid #e5e7eb",
        borderRadius: 12
      }}
    >
      <div style={{ display: "grid", gap: 4 }}>
        <strong>{attachment.displayName}</strong>
        <div style={{ fontSize: 13, opacity: 0.75 }}>{describeStatus(attachment.attachmentType)}</div>
      </div>
      {attachment.attachmentType === "image" && attachment.downloadUrl ? (
        <img
          src={attachment.downloadUrl}
          alt={attachment.displayName}
          style={{ width: "100%", borderRadius: 10, objectFit: "cover" }}
        />
      ) : null}
      {attachment.attachmentType === "video" && attachment.downloadUrl ? (
        <video
          controls
          preload="metadata"
          src={attachment.downloadUrl}
          style={{ width: "100%", borderRadius: 10 }}
        />
      ) : null}
      {attachment.downloadUrl ? (
        <a href={attachment.downloadUrl} target="_blank" rel="noreferrer">
          {linkLabel}
        </a>
      ) : (
        <span style={{ fontSize: 13, opacity: 0.75 }}>添付リンクを準備中です。</span>
      )}
    </article>
  );
}

export function PublicTopPage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { tenant, loadError, loadState } = usePublicTenantState(tenantSlug);

  return (
    <PageScaffold
      title={resolvePublicTenantTitle(tenant)}
      description="公開トップページ"
      links={buildPublicLinks(tenantSlug, tenant)}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {loadState === "loading" ? <p style={{ margin: 0 }}>公開情報を読み込んでいます。</p> : null}
        {loadError ? <p style={{ margin: 0 }}>{loadError}</p> : null}
        <PublicTenantHeader tenantSlug={tenantSlug} tenant={tenant} />
      </div>
    </PageScaffold>
  );
}

export function PublicDemoPage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { tenant, loadError, loadState } = usePublicTenantState(tenantSlug);
  const tenantLabels = resolvePublicTenantLabels(tenant);
  const [schedule, setSchedule] = useState<PublicScheduleResponseDto | null>(null);
  const [scheduleState, setScheduleState] = useState<"idle" | "loading" | "error">("idle");
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  useEffect(() => {
    if (!canUsePublicDemoViewer(tenantSlug, tenant)) {
      setSchedule(null);
      setScheduleState("idle");
      setScheduleError(null);
      return;
    }

    let cancelled = false;

    async function loadSchedule() {
      setScheduleState("loading");
      setScheduleError(null);

      try {
        const nextSchedule = await fetchPublicSchedule(tenantSlug);

        if (cancelled) {
          return;
        }

        setSchedule(nextSchedule);
        setScheduleState("idle");
      } catch {
        if (cancelled) {
          return;
        }

        setSchedule(null);
        setScheduleState("error");
        setScheduleError("デモ予定の取得に失敗しました。");
      }
    }

    void loadSchedule();

    return () => {
      cancelled = true;
    };
  }, [tenant, tenantSlug]);

  if (loadState === "loading") {
    return (
      <PageScaffold title="デモ画面" description="デモ情報を読み込んでいます。">
        <p style={{ margin: 0 }}>デモ情報を読み込んでいます。</p>
      </PageScaffold>
    );
  }

  if (loadError || !canUsePublicDemoViewer(tenantSlug, tenant)) {
    return (
      <PageScaffold title="ページが見つかりません" description="このデモ画面は利用できません。">
        <p style={{ margin: 0 }}>このデモ画面は利用できません。</p>
      </PageScaffold>
    );
  }

  const nextScheduleItems = schedule?.items.slice(0, 3) ?? [];
  const timeZone = schedule?.timeZone ?? "Asia/Tokyo";

  return (
    <PageScaffold
      title="デモ画面"
      description="この画面はデモです。実際に利用する場合は「利用開始」からLINEログインしてください。"
      links={[
        { to: `/t/${tenantSlug}`, label: "デモトップへ戻る" },
        { to: `/t/${tenantSlug}/entry`, label: "利用開始" }
      ]}
    >
      <div style={{ display: "grid", gap: 20 }}>
        <section style={{ display: "grid", gap: 8 }}>
          <strong>デモ{tenantLabels.student}ホーム</strong>
          <p style={{ margin: 0 }}>
            {tenantLabels.lesson}予定・動画・通知の見え方を、ログインなしのサンプルとして確認できます。
          </p>
          <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
            デモ画面では編集、アップロード、招待処理、欠席連絡などの操作は利用できません。
          </p>
        </section>

        <section style={{ display: "grid", gap: 8 }}>
          <strong>今後の{tenantLabels.lesson}予定</strong>
          {scheduleState === "loading" ? <p style={{ margin: 0 }}>予定を読み込んでいます。</p> : null}
          {scheduleError ? <p style={{ margin: 0 }}>{scheduleError}</p> : null}
          {nextScheduleItems.length === 0 ? (
            <p style={{ margin: 0 }}>表示できる予定サンプルはありません。</p>
          ) : (
            nextScheduleItems.map((item) => (
              <article
                key={item.occurrenceId}
                style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 12, display: "grid", gap: 4 }}
              >
                <strong>{item.lessonTitle}</strong>
                <div>{formatTenantDateTimeRange(item.startAt, item.endAt, timeZone)}</div>
                <div>{item.studioName ?? `${tenantLabels.studio}未定`}</div>
                {item.publicNote ? <div style={{ fontSize: 14, opacity: 0.8 }}>{item.publicNote}</div> : null}
              </article>
            ))
          )}
        </section>

        <section style={{ display: "grid", gap: 8 }}>
          <strong>動画サンプル</strong>
          {buildDemoHomeVideos(tenantLabels).map((video) => (
            <article
              key={video.title}
              style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 12, display: "grid", gap: 4 }}
            >
              <strong>{video.title}</strong>
              <div style={{ fontSize: 14, opacity: 0.8 }}>{video.description}</div>
              <div style={{ fontSize: 13, opacity: 0.75 }}>この操作はデモ画面では利用できません。</div>
            </article>
          ))}
        </section>

        <section style={{ display: "grid", gap: 8 }}>
          <strong>通知サンプル</strong>
          {buildDemoHomeNotifications(tenantLabels).map((message) => (
            <article key={message} style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 12 }}>
              {message}
            </article>
          ))}
        </section>
      </div>
    </PageScaffold>
  );
}

export function PublicSchedulePage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { tenant, loadError: tenantError } = usePublicTenantState(tenantSlug);
  const [schedule, setSchedule] = useState<PublicScheduleResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const tenantLabels = resolvePublicTenantLabels(tenant);

  useEffect(() => {
    let cancelled = false;

    async function loadSchedule() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextSchedule = await fetchPublicSchedule(tenantSlug);

        if (cancelled) {
          return;
        }

        setSchedule(nextSchedule);
        setLoadState("idle");
      } catch {
        if (cancelled) {
          return;
        }

        setSchedule(null);
        setLoadState("error");
        setLoadError("公開スケジュールの取得に失敗しました。");
      }
    }

    void loadSchedule();

    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  return (
    <PageScaffold
      title="公開スケジュール"
      description={`現在公開中の${tenantLabels.lesson}予定です。`}
      links={buildPublicLinks(tenantSlug, tenant)}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {loadState === "loading" ? <p style={{ margin: 0 }}>公開スケジュールを読み込んでいます。</p> : null}
        {tenantError ? <p style={{ margin: 0 }}>{tenantError}</p> : null}
        {loadError ? <p style={{ margin: 0 }}>{loadError}</p> : null}
        {schedule && schedule.items.length === 0 ? <p style={{ margin: 0 }}>現在公開中のスケジュールはありません。</p> : null}
        {schedule?.monthGroups.map((group) => (
          <section key={group.yearMonth} style={{ display: "grid", gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>{formatTenantMonthHeading(group.yearMonth, schedule.timeZone)}</h2>
            {group.items.map((item) => (
              <article
                key={item.occurrenceId}
                style={{
                  display: "grid",
                  gap: 8,
                  padding: 12,
                  border: "1px solid #e5e7eb",
                  borderRadius: 12
                }}
              >
                <strong>{item.lessonTitle}</strong>
                <div style={{ fontSize: 14 }}>
                  {formatTenantDateTimeRange(item.startAt, item.endAt, schedule.timeZone)}
                </div>
                <div style={{ fontSize: 14 }}>{item.studioName ?? `${tenantLabels.studio}未定`}</div>
                {renderFlags(item.displayFlags, tenantLabels)}
                {item.publicNote ? <div style={{ fontSize: 14, opacity: 0.85 }}>{item.publicNote}</div> : null}
              </article>
            ))}
          </section>
        ))}
      </div>
    </PageScaffold>
  );
}

export function PublicEventsPage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { tenant, loadError: tenantError } = usePublicTenantState(tenantSlug);
  const [events, setEvents] = useState<PublicEventsResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadEvents() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextEvents = await fetchPublicEvents(tenantSlug);

        if (cancelled) {
          return;
        }

        setEvents(nextEvents);
        setLoadState("idle");
      } catch {
        if (cancelled) {
          return;
        }

        setEvents(null);
        setLoadState("error");
        setLoadError("公開イベントの取得に失敗しました。");
      }
    }

    void loadEvents();

    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  return (
    <PageScaffold
      title="公開イベント"
      description="現在公開中のイベント一覧です。"
      links={buildPublicLinks(tenantSlug, tenant)}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {loadState === "loading" ? <p style={{ margin: 0 }}>公開イベントを読み込んでいます。</p> : null}
        {tenantError ? <p style={{ margin: 0 }}>{tenantError}</p> : null}
        {loadError ? <p style={{ margin: 0 }}>{loadError}</p> : null}
        {events && events.items.length === 0 ? <p style={{ margin: 0 }}>現在公開中のイベントはありません。</p> : null}
        {events?.items.map((item) => (
          <Link
            key={item.eventId}
            to={`/t/${tenantSlug}/public/events/${item.eventId}`}
            style={{
              display: "grid",
              gap: 8,
              padding: 12,
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              color: "inherit",
              textDecoration: "none"
            }}
          >
            <strong>{item.title}</strong>
            {item.summary ? <div style={{ fontSize: 14, opacity: 0.85 }}>{item.summary}</div> : null}
            {(item.publishFrom || item.publishTo) ? (
              <div style={{ fontSize: 13, opacity: 0.75 }}>
                公開期間: {item.publishFrom ? formatTenantDateTime(item.publishFrom, events.timeZone) : "開始未設定"}
                {" 〜 "}
                {item.publishTo ? formatTenantDateTime(item.publishTo, events.timeZone) : "終了未設定"}
              </div>
            ) : null}
            {item.attachments.length > 0 ? (
              <div style={{ fontSize: 13, opacity: 0.75 }}>添付 {item.attachments.length}件</div>
            ) : null}
          </Link>
        ))}
      </div>
    </PageScaffold>
  );
}

export function PublicEventDetailPage() {
  const { tenantSlug = "unknown-tenant", eventId = "unknown-event" } = useParams();
  const { tenant, loadError: tenantError } = usePublicTenantState(tenantSlug);
  const [event, setEvent] = useState<PublicEventDetailDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadEvent() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextEvent = await fetchPublicEvent(tenantSlug, eventId);

        if (cancelled) {
          return;
        }

        setEvent(nextEvent);
        setLoadState("idle");
      } catch {
        if (cancelled) {
          return;
        }

        setEvent(null);
        setLoadState("error");
        setLoadError("公開イベントの詳細を取得できませんでした。");
      }
    }

    void loadEvent();

    return () => {
      cancelled = true;
    };
  }, [eventId, tenantSlug]);

  const links = useMemo(() => buildPublicLinks(tenantSlug, tenant), [tenant, tenantSlug]);

  return (
    <PageScaffold
      title={event?.title ?? "公開イベント詳細"}
      description="公開イベントの詳細"
      links={links}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {loadState === "loading" ? <p style={{ margin: 0 }}>公開イベント詳細を読み込んでいます。</p> : null}
        {tenantError ? <p style={{ margin: 0 }}>{tenantError}</p> : null}
        {loadError ? <p style={{ margin: 0 }}>{loadError}</p> : null}
        {event ? (
          <>
            {event.summary ? <p style={{ margin: 0 }}>{event.summary}</p> : null}
            {(event.publishFrom || event.publishTo) ? (
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                公開期間: {event.publishFrom ? formatTenantDateTime(event.publishFrom, event.timeZone) : "開始未設定"}
                {" 〜 "}
                {event.publishTo ? formatTenantDateTime(event.publishTo, event.timeZone) : "終了未設定"}
              </div>
            ) : null}
            {event.body ? (
              <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{event.body}</div>
            ) : null}
            {event.attachments.length > 0 ? (
              <section style={{ display: "grid", gap: 8 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>添付</h2>
                {event.attachments.map((attachment) => renderPublicAttachmentCard(attachment))}
              </section>
            ) : null}
          </>
        ) : null}
      </div>
    </PageScaffold>
  );
}
